// --------------------------------------------------------------
// Created On: 2023-10-2
// Author: Zachary Thomas
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment, useState } from "react";
import IconTooltip from "../../IconTooltip/IconTooltip";
import Success from "src/components/Success/Success";
import isoIsValid from "../../../utilities/time/isoIsValid";
import formatDateLocal from "../../../utilities/time/formatDateLocal";
import userHasPermission from "src/utilities/userHasPermission";
import { APPLY_FIRMWARE_PERMISSION } from "src/constants/permissions";
import ConfirmFirmwareUpdateModal from "src/components/ConfirmFirmwareUpdateModal/ConfirmFirmwareUpdateModal";
import styles from "./DeviceStatusValue.module.scss";

// A row that shows the value and description for a specific type of device status.
export default function DeviceStatusValue(props: Props): Component {
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  // Close the child update confirmation modal and let the parent know that a firmware update has started.
  function handleFirmwareUpdate(): void {
    props.onUpdateFirmware();
    setShowUpdateModal(false);
  }

  return (
    <Fragment>
      <tr className={styles.body}>
        <td>
          <label className={styles.label}>{props.name}</label>
        </td>
        <td>
          <div className="row">
            <div className="col">
              {isoIsValid(props.value.split(".")[0]) ? formatDateLocal(props.value) : props.value}
              {props.name === "RSSI" && <span> dBm</span>}
            </div>
            {props.name === "Firmware Version" && userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) && (
              <Fragment>
                <div className="col">
                  {!props.firmwareUpdateInitialized && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={props.loading || props.firmwareUpdateInitialized || props.onLatestFirmware === true}
                      onClick={() => setShowUpdateModal(true)}
                    >
                      Update Firmware
                    </button>
                  )}
                </div>
                {props.firmwareUpdateInitialized && (
                  <span className="pt-2">
                    <Success message="Firmware update initialized." />
                  </span>
                )}
              </Fragment>
            )}
          </div>
        </td>
        <td>
          <IconTooltip
            id={`status-row-${props.name}`}
            icon="info-circle"
            message={props.description}
            color="var(--info-tooltip)"
          />
        </td>
      </tr>
      <ConfirmFirmwareUpdateModal
        showModal={showUpdateModal}
        deviceId={props.deviceId}
        onClose={() => setShowUpdateModal(false)}
        onUpdate={() => handleFirmwareUpdate()}
        onCancel={() => setShowUpdateModal(false)}
      />
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  firmwareUpdateInitialized: boolean;
  name: string;
  deviceId: number | null;
  description: string;
  value: string;
  onLatestFirmware: boolean | null;
  onUpdateFirmware: () => void;
}
